import styles from './nav-menu.module.scss';
import { Button, Menu } from 'antd';
import type { MenuProps } from 'antd';
import { NavLink, useLocation } from 'react-router-dom';
import { MenuKey, MenuKeys } from './nav-menu.types';
import Icon from '@ant-design/icons';
import { useLogout } from '@/hooks/use-logout/use-logout';
import { navigationData } from './nav-menu.constans';
import { useUser } from '@/hooks/use-user/use-user';
import { UserRole } from '@/services/user-service/user.service.types';

interface NavMenuProps {
  onMenuItemClick?: () => void;
}

export function NavMenu({ onMenuItemClick }: NavMenuProps) {
  const { logout } = useLogout();
  const { pathname } = useLocation();
  const { userData } = useUser();

  const hasReviewerRole =
    userData?.roles?.some((role) => role.name === ('reviewer' as UserRole)) || false;

  const isAdminOrCompanyAdmin =
    userData?.roles?.some(
      (role) => role.name === ('admin' as UserRole) || role.name === ('company_admin' as UserRole)
    ) || false;

  const selectedKey = pathname === '/' ? 'dashboard' : pathname.substring(1).split('/')[0];

  const getColors = (currentKey: MenuKey, label: string) => {
    if (currentKey === selectedKey) {
      return { iconColor: styles.activeIconColor, backgroundColor: styles.activeBackgroundColor };
    } else if (label === 'Logout') {
      return {
        iconColor: styles.logoutIconColor,
        backgroundColor: styles.logoutBackgroundIconColor,
      };
    } else {
      return { iconColor: styles.iconColor, backgroundColor: styles.defaultBackgroundColor };
    }
  };

  const filteredNavigationData = navigationData.filter(({ label }) => {
    if (
      hasReviewerRole &&
      (label === 'Surveys' || label === 'Work Orders' || label === 'Profile')
    ) {
      return false;
    }
    if (label === 'Company') {
      return isAdminOrCompanyAdmin;
    }
    return true;
  });

  const items: MenuProps['items'] = filteredNavigationData.map(({ label, IconComponent }) => {
    const menuKeyIndex = navigationData.findIndex((item) => item.label === label);
    const menuKey = MenuKeys[menuKeyIndex];

    return {
      label:
        label === 'Logout' ? (
          <Button
            role="button"
            className={styles.logoutButton}
            type="link"
            onClick={async () => await logout()}
          >
            <div
              className={styles.menuBackgroundItem}
              style={{ backgroundColor: getColors(menuKey, label).backgroundColor }}
            >
              <Icon
                style={{
                  stroke: getColors(menuKey, label).iconColor,
                  color: getColors(menuKey, label).backgroundColor,
                }}
                width={16}
                height={16}
                component={IconComponent}
              />
            </div>
            Logout
          </Button>
        ) : (
          <NavLink onClick={onMenuItemClick} to={label === 'Dashboard' ? '/' : `/${menuKey}`}>
            {label}
          </NavLink>
        ),
      key: menuKey,
      icon: !(label === 'Logout') && (
        <div
          className={styles.menuBackgroundItem}
          style={{ backgroundColor: getColors(menuKey, label).backgroundColor }}
        >
          <Icon
            style={
              label === 'Work Orders'
                ? { color: getColors(menuKey, label).iconColor }
                : {
                    stroke: getColors(menuKey, label).iconColor,
                    color: getColors(menuKey, label).backgroundColor,
                  }
            }
            width={16}
            height={16}
            component={IconComponent}
          />
        </div>
      ),
    };
  });

  return (
    <Menu mode="vertical" selectedKeys={[selectedKey]} className={styles.menu} items={items} />
  );
}
